import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useAppSelector } from '@/store/hooks';
import { CREW_PER_PAGE } from './crew-content-constants';
import { ESortOrderMap } from '@ui/buttons/sort-button/types';
import { getDateWithCache } from '@/utils/dates';
import { getActiveChannel } from '@/store/slices/user/selectors';
import { EPermissions } from '@/types/permissions.types';
import useSelectSessionCrew from '../../../helpers/use-select-session-crew';

const useSortedAndPagedCrewList = (props?: { page?: number }) => {
  const page = props?.page || 1;
  const [searchParams] = useSearchParams();

  const activeChannel = useAppSelector(getActiveChannel);

  const crew = useSelectSessionCrew();

  const sortedCrew = useMemo(() => {
    if (!crew?.length) return null;
    if (!activeChannel) return null;

    const sortBy = searchParams.get('sortBy');
    const _sortOrder = Number(searchParams.get('sortOrder'));
    const sortOrder = isNaN(_sortOrder) ? 0 : _sortOrder;
    return crew.toSorted((aData, bData) => {
      const adminA = aData.bl_admin;
      const adminB = bData.bl_admin;
      if (!adminA || !adminB) return 0;

      const adminAName = `${adminA.profile.first_name} ${adminA.profile.last_name}`.trim();
      const adminBName = `${adminB.profile.first_name} ${adminB.profile.last_name}`.trim();

      if (!sortBy || !sortOrder || sortBy === 'name') {
        if (sortOrder === ESortOrderMap.desc) {
          return adminBName.toLowerCase().localeCompare(adminAName.toLowerCase());
        }
        return adminAName.toLowerCase().localeCompare(adminBName.toLowerCase());
      }

      if (sortBy === 'role') {
        const rolesA = adminA?.channels[activeChannel].filter((role) =>
          [EPermissions.Producer, EPermissions.Presenter].includes(role as EPermissions)
        );
        const rolesB = adminB?.channels[activeChannel].filter((role) =>
          [EPermissions.Producer, EPermissions.Presenter].includes(role as EPermissions)
        );
        if (!rolesA?.length) return 0;
        if (!rolesB?.length) return -1;
        if (sortOrder === ESortOrderMap.desc) {
          // sort in desc and get the first available
          const roleA = rolesA.toSorted((roleA, roleB) => roleB.localeCompare(roleA))?.[0];
          const roleB = rolesB.toSorted((roleA, roleB) => roleB.localeCompare(roleA))?.[0];
          return roleB.localeCompare(roleA);
        }
        const roleA = rolesA.toSorted((roleA, roleB) => roleA.localeCompare(roleB))?.[0];
        const roleB = rolesB.toSorted((roleA, roleB) => roleA.localeCompare(roleB))?.[0];
        return roleA.localeCompare(roleB);
      }

      if (sortBy === 'email') {
        if (sortOrder === ESortOrderMap.desc) {
          return adminB?.email.toLowerCase().localeCompare(adminA.email.toLowerCase());
        }
        return adminA?.email.toLowerCase().localeCompare(adminB?.email.toLowerCase());
      }
      if (sortBy === 'last_login') {
        if (!adminA.last_login) return 0;
        if (!adminB.last_login) return -1;
        const cachedDateA = getDateWithCache(adminA.last_login || 0);
        const cachedDateB = getDateWithCache(adminB.last_login || 0);
        if (sortOrder === ESortOrderMap.desc) {
          return cachedDateA.getTime() - cachedDateB.getTime();
        }
        return cachedDateB.getTime() - cachedDateA.getTime();
      }

      // if no sort order set just return by name so we have some sort of reasonable default
      return adminAName.toLowerCase().localeCompare(adminBName.toLowerCase());
    });
  }, [activeChannel, searchParams, crew]);

  const pagedCrew = useMemo(() => {
    const startIdx = Math.max(page - 1, 0);
    return sortedCrew?.slice(startIdx * CREW_PER_PAGE, startIdx * CREW_PER_PAGE + CREW_PER_PAGE);
  }, [sortedCrew, page]);

  return {
    sortedCrew,
    pagedCrew,
  };
};

export default useSortedAndPagedCrewList;
